import {
  Environment,
  FactoryStatus,
  Operation,
  Packet,
  PacketManager,
  PacketType,
} from 'admin/utils/devices/payregi/packet';
import serial from 'admin/utils/devices/serial';

// Packet IDs 0..10 are reserved for use by the connector
const _minPacketId = 10;

// Payregi uses the stock CP2102N VID and PID.
const _payregiUSBVid = 0x10c4;
const _payregiUSBPid = 0xea60;

type PayregiConnectorOptions = {
  onReceivePacket?: (packet: Packet) => void | Promise<void>;
  onDeviceDisconnected?: () => void | Promise<void>;
  onReceiveError?: (error: Error) => void | Promise<void>;
  baudRate?: number;
};

export class PayregiConnector {
  private _port: any;
  private _pm: PacketManager | null;
  private _pid: number;
  private _decoder: TextDecoder;
  private readonly _pidResolvers: { [key: number]: (packet: Packet) => void };
  public onReceivePacket: (packet: Packet) => void | Promise<void>;
  public onDeviceDisconnected: () => void | Promise<void>;
  public onReceiveError: (error: Error) => void | Promise<void>;
  public baudRate: number;

  constructor({
    onReceivePacket = (_packet: Packet) => {
      console.log('received packet');
    },
    onDeviceDisconnected = () => {},
    onReceiveError = (error: Error) => {
      console.error('Device communication error:', error);
    },
    baudRate = 9600,
  }: PayregiConnectorOptions = {}) {
    this._port = null;
    this._pm = null;
    this.onReceivePacket = onReceivePacket;
    this.onDeviceDisconnected = onDeviceDisconnected;
    this.onReceiveError = onReceiveError;
    this._pid = _minPacketId;
    this._decoder = new TextDecoder();
    this.baudRate = baudRate || 115200;
    // This is a table mapping packet IDs and promise resolvers
    this._pidResolvers = {};
  }

  async requestConnection(): Promise<boolean> {
    if (!serial.isWebSerialSupported()) {
      console.info('Web Serial API is not supported in this browser');
      return false;
    }
    await this.disconnect();
    console.info('Requesting serial port access...');
    this._port = await serial.requestSerialPort({
      usbVendorId: _payregiUSBVid,
      usbProductId: _payregiUSBPid,
    });

    this._pm = new PacketManager();
    this._port.onReceive = (data: Uint8Array<ArrayBufferLike>) => this._onReceiveData(data);
    this._port.onReceiveError = (error: Error) => this._onReceiveError(error);
    await this._port.connect({ baudRate: this.baudRate });
    return true;
  }
  async reconnectToPort(port: any): Promise<boolean> {
    if (!serial.isWebSerialSupported()) {
      console.info('Web Serial API is not supported in this browser');
      return false;
    }
    await this.disconnect();
    console.info('Reconnecting to saved port...');
    this._port = port;
    this._pm = new PacketManager();
    this._port.onReceive = (data: Uint8Array<ArrayBufferLike>) => this._onReceiveData(data);
    this._port.onReceiveError = (error: Error) => this._onReceiveError(error);

    try {
      await this._port.connect({ baudRate: this.baudRate });
      return true;
    } catch (error) {
      console.error('Failed to reconnect to port:', error);
      this._port = null;
      this._pm = null;
      return false;
    }
  }

  isConnected(): boolean {
    return this._port?.isConnected || false;
  }

  async disconnect(): Promise<void> {
    if (this._port) {
      try {
        if (this._port.isConnected) {
          await this._port.disconnect();
        }
      } catch (error) {
        console.error('Error during disconnect:', error);
      }
      this._port = null;
      this._pm = null;
    }
  }

  async _onReceiveData(data: Uint8Array): Promise<void> {
    if (!this._pm) return;
    this._pm.writeBytes(data);
    let packet = this._pm.parsePacket();
    while (packet) {
      await this._onReceivePacket(packet);
      packet = this._pm.parsePacket();
    }
  }

  async _onReceivePacket(packet: Packet): Promise<void> {
    const resolver = this._pidResolvers[packet.packetId];
    if (resolver) {
      resolver(packet);
    } else if (packet.packetId >= _minPacketId) {
      await this.onReceivePacket(packet);
    } else {
      // This is for handling communication done internally by the connector.
    }
  }

  async _onReceiveError(error: Error): Promise<void> {
    if (
      error.message &&
      ['device has been lost', 'disconnected', 'The device has been lost'].some((msg) =>
        error.message.includes(msg)
      )
    ) {
      console.info('USB device has been disconnected');
      await this.disconnect();
      await this.onDeviceDisconnected();
    } else {
      await this.onReceiveError(error);
      console.error(`Receive error: ${error.message}`);
    }
  }

  /// Sends a request with op: Operation, payloadBytes: Uint8List
  async sendRequest(op: number, payloadBytes?: Uint8Array): Promise<Packet> {
    const data = payloadBytes || new Uint8Array(0);
    const packet = new Packet();
    packet.type = PacketType.TYPE_REQUEST;
    packet.senderId = 0;
    packet.destinationId = 1;
    packet.packetId = this._pid;
    packet.op = op;
    packet.len = data.length;
    packet.data = data;

    await this._sendPacket(packet);
    return packet;
  }

  async command(op: number, payloadBytes?: Uint8Array): Promise<Packet> {
    const promise = new Promise<Packet>((resolve) => {
      this._pidResolvers[this._pid] = resolve;
    });
    await this.sendRequest(op, payloadBytes);
    return await promise;
  }

  async ping(): Promise<{ protocol: number }> {
    const { data } = await this.command(Operation.OP_PING, undefined);
    // OP_PING returns protocol version in first byte
    return { protocol: data[0] };
  }

  async getSerialNumber(): Promise<string> {
    try {
      const { data } = await this.command(Operation.OP_GET_SERIAL_NUMBER, undefined);
      const serialNumber = this._decoder.decode(data);

      return serialNumber;
    } catch (error) {
      console.error('Failed to get serial number:', error);
      throw error;
    }
  }

  async getAccountConfigUrl(): Promise<string> {
    const { data } = await this.command(Operation.OP_GET_DEVICE_CONFIG_URL);
    return this._decoder.decode(data);
  }

  async configureAccount(configData: Uint8Array): Promise<boolean> {
    const { data } = await this.command(Operation.OP_SET_DEVICE_CONFIG, configData);
    return data[0] == 0;
  }

  async getDeviceInfo(): Promise<any> {
    const { data } = await this.command(Operation.OP_GET_DEVICE_INFO, undefined);
    const dec = new TextDecoder();
    const txt = dec.decode(data);

    return JSON.parse(txt);
  }

  getEnvironment(env: number): string {
    const environmentMap: { [key: number]: string } = {
      [Environment.PRODUCTION]: '本番環境 (Production)',
      [Environment.SANDBOX]: 'サンドボックス環境 (Sandbox)',
      [Environment.TOPIC1]: 'TOPIC1環境 (Topic1)',
      [Environment.QA]: '検証環境 (QA)',
      [Environment.DEVELOP]: '開発環境 (Develop)',
      [Environment.LOADTEST]: '負荷テスト環境 (LoadTest)',
    };
    return environmentMap[env] || '不明';
  }

  async previewVolume(volume: number): Promise<boolean> {
    return await this.setDeviceSettings({ volume, save: false, play_sound: true });
  }

  async previewLuminance(luminance: number): Promise<boolean> {
    return await this.setDeviceSettings({ brightness: luminance, save: false });
  }

  async setDeviceSettings({
    volume,
    brightness,
    environment,
    play_sound,
    save,
  }: {
    volume?: number;
    brightness?: number;
    environment?: number;
    play_sound?: boolean;
    save?: boolean;
  } = {}): Promise<boolean> {
    // Check factory status before calling getDeviceInfo
    // OP_GET_DEVICE_INFO requires factory status >= AGING_COMPLETED
    // const factoryStatus = await this.getFactoryStatus();

    let currentEnv = 0xff;
    let currentVolume = 0xff;
    let currentBrightness = 0xff;

    // if (factoryStatus >= FactoryStatus.AGING_COMPLETED) {
    //   // Device is past aging, we can get current settings
    //   const info = await this.getDeviceInfo();
    //   currentEnv = info.app_env;
    //   currentVolume = info.speaker_volume;
    //   currentBrightness = info.lcd_luminance;
    // }

    const payload = new Uint8Array(5);
    payload[0] = environment == null || environment == currentEnv ? 0xff : environment;
    payload[1] = volume == null || volume == currentVolume ? 0xff : volume;
    payload[2] = brightness == null || brightness == currentBrightness ? 0xff : brightness;
    payload[3] = play_sound ? 1 : 0;
    payload[4] = save ? 1 : 0;
    const { data } = await this.command(Operation.OP_SET_DEVICE_SETTINGS, payload);

    return data[0] === 0;
  }

  async fetchAndApplyAccountConfigToDevice(): Promise<boolean> {
    const url = await this.getAccountConfigUrl();
    if (url.length === 0) return false;
    const response = await fetch(url);
    const bodyJson = await response.json();

    if (bodyJson.has_settings) {
      const data = bodyJson.data;
      return await this.configureAccount(data);
    }
    return false;
  }

  // Factory Process Methods
  async getFactoryStatus(): Promise<(typeof FactoryStatus)[keyof typeof FactoryStatus]> {
    const { data } = await this.command(Operation.OP_GET_FACTORY_STATUS, undefined);
    return data[0] as (typeof FactoryStatus)[keyof typeof FactoryStatus];
  }

  async showCharacterFP(char: string, position: number): Promise<void> {
    const payload = new Uint8Array(2);
    payload[0] = char.charCodeAt(0);
    payload[1] = position;
    await this.command(Operation.OP_SHOW_CHARACTER_FP, payload);
  }

  async showQRCodeFP(data: string): Promise<void> {
    const encoder = new TextEncoder();
    const payload = encoder.encode(data);
    await this.command(Operation.OP_SHOW_QRCODE_FP, payload);
  }

  async writeNFCReadValueFP(value: string): Promise<void> {
    const encoder = new TextEncoder();
    const payload = encoder.encode(value);
    await this.command(Operation.OP_WRITE_NFC_READ_VALUE_FP, payload);
  }

  async setLEDColorFP(color: { r: number; g: number; b: number }): Promise<void> {
    const payload = new Uint8Array(3);
    payload[0] = color.r;
    payload[1] = color.g;
    payload[2] = color.b;
    await this.command(Operation.OP_SET_LED_COLOR_FP, payload);
  }

  async emitSoundFP(sound: number, power: number): Promise<void> {
    const payload = new Uint8Array(2);
    payload[0] = sound;
    payload[1] = power;
    await this.command(Operation.OP_EMIT_SOUND_FP, payload);
  }

  async finalizeFP(): Promise<void> {
    await this.command(Operation.OP_FINALIZE_FP, undefined);
  }

  async _sendPacket(packet: Packet): Promise<void> {
    try {
      const serializedPacket = packet.serialize();
      await this._port.send(serializedPacket);
      this._pid = Math.max(_minPacketId, (this._pid + 1) % 256);
    } catch (error) {
      console.error('Failed to send packet:', error);
      throw error;
    }
  }
}
